/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react'
import { Paper, IconButton, Tooltip, Badge, List, ListItem, Divider, Card, CardContent, Typography, Grid, Box, Container, LinearProgress, Accordion, AccordionSummary, AccordionDetails, Drawer } from '@mui/material'

import { Info, ExpandMore, Stars } from '@mui/icons-material'
import './main.css'
import { getNextLevelRewards, sortChallenges, Gauge, CategorySelect } from './utils.js'

const ChallengeCard = ({ challenge }) => {
  const { percentage, nextThreshold, value, percentile, challengeId, localizedName, level, achievedTime, matchesForNextLevel } = challenge

  const imagePath = level === 0
    ? `/photos/challenge/${challengeId}-IRON.png`
    : `/photos/challenge/${challengeId}-${level}.png`

  const nextLevelRewards = getNextLevelRewards(level, challengeId)

  return (
    <div className="challenge-card">
      <div className="card-front">
        <Gauge value={percentage} imageSrc={imagePath} type="challenge" size={80} />
        <div style={{ marginTop: 10, width: '80px' }}>
          <LinearProgress
            variant="determinate"
            value={(value / nextThreshold) * 100}
            color="primary"
            style={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="body2" style={{ fontSize: 12, marginTop: 4 }}>{`${value} / ${nextThreshold}`}</Typography>
        </div>
        <Typography style={{ marginBottom: '10px', marginTop: '8px' }}>
          {localizedName.name}
        </Typography>
        <Typography style={{ fontSize: '0.8rem' }}> {localizedName.description}</Typography>

        {matchesForNextLevel > 0 &&
        <Typography className="matches-for-next-level">
          <span className="matches-text">Avg. Matches for next level:</span>
          <span className="matches-number" title={isFinite(matchesForNextLevel) ? `${matchesForNextLevel} matches for next level` : 'No Info'}>
      {isFinite(matchesForNextLevel) ? matchesForNextLevel : 'No Info'}
    </span>
        </Typography>
        }
      </div>
      <div className="card-back">
        <img src={imagePath} alt={localizedName.en} className="card-icon" />
        <Typography style={{ fontSize: '12px', marginBottom: '10px' }}>
          World spot: {(percentile * 100).toFixed(1)} %
        </Typography>
        <Typography style={{ fontSize: '12px', marginBottom: '10px' }}>
          Achieved Time: {achievedTime ? new Date(achievedTime).toLocaleString() : 'Not achieved'}
        </Typography>
        {nextLevelRewards && (
          <Typography style={{ fontSize: '12px', marginBottom: '10px' }}>Next Level Rewards: {nextLevelRewards}</Typography>
        )}
      </div>
    </div>
  )
}

const ChallengesList = ({ challenges }) => {
  const doneLevels = ['MASTER', 'GRANDMASTER', 'CHALLENGER']
  const levelOrder = ['DIAMOND', 'PLATINUM', 'GOLD', 'SILVER', 'BRONZE', 'IRON', 0]
  const challengeCategories = {
    'ARAM Warrior': ['ARAM Warrior', 'Double Decimation', 'ARAM Legend', 'Solo Carry', 'Farm Champs Not Minions', 'Bad Medicine', 'No Hiding', 'ARAM Eradication'],
    'ARAM Finesse': ['ARAM Finesse', 'Pop Goes the Poro', 'Free Ticket to Base', 'Free Money', 'Another Day, Another Bullseye', 'It was a... Near-Hit', 'Snow Day'],
    'ARAM Champion': ['ARAM Champion', 'Active Participant', 'Rapid Demolition', 'All Random All Champions', 'All Random All Flawless', 'NA-RAM', 'Can\'t Touch This', 'Lightning Round']
  }

  const [categoryOption, setCategoryOption] = useState('')
  const [openSections, setOpenSections] = useState({})

  useEffect(() => {
    const initialOpenSections = {
      done: false,
      notDone: true
    }
    setOpenSections(initialOpenSections)
  }, [])

  const filterChallenges = (challenges, isDone) =>
    challenges.filter((challenge) =>
      isDone
        ? doneLevels.includes(challenge.level) || (challenge.challengeId === 101206 && challenge.level === 'BRONZE')
        : !doneLevels.includes(challenge.level) && !(challenge.challengeId === 101206 && challenge.level === 'BRONZE')
    )

  const sortChallengesByLevelOrder = (challenges) =>
    challenges.sort((a, b) => levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level))

  const groupChallengesByDivision = (challenges) => {
    return challenges.reduce((groups, challenge) => {
      const division = challenge.level
      if (!groups[division]) {
        groups[division] = []
      }
      groups[division].push(challenge)
      return groups
    }, {})
  }

  const filterChallengesByCategory = (challenges, category) => {
    if (!category) return challenges
    const categoryChallenges = challengeCategories[category]
    return challenges.filter((challenge) => categoryChallenges.includes(challenge.localizedName.name))
  }

  const doneChallenges = sortChallengesByLevelOrder(filterChallenges(challenges, true))
  const sortedChallenges = sortChallenges(challenges)
  const filteredChallenges = filterChallengesByCategory(sortedChallenges, categoryOption)
  const notDoneChallenges = groupChallengesByDivision(sortChallengesByLevelOrder(filterChallenges(filteredChallenges, false)))

  const toggleSection = (section) =>
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }))

  const handleCategoryChange = (event) => {
    setCategoryOption(event.target.value)
  }

  const renderChallenges = (challenges, title, section) => (
    <Accordion expanded={!!openSections[section]} onChange={() => toggleSection(section)} sx={{ mb: openSections[section] ? 0 : 2 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
      <Typography variant="h5">{title}</Typography>
      {section === 'notDone' && !!openSections[section] && (
        <Box sx={{ ml: 'auto', display: 'flex', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          <CategorySelect categoryOption={categoryOption} handleCategoryChange={handleCategoryChange} challengeCategories={challengeCategories}/>
        </Box>
      )}
    </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ flexGrow: 2 }}>
          <Grid container spacing={3}>
            {Array.isArray(challenges)
              ? (
                  challenges.map((challenge) => (
                <Grid item xs={12} sm={6} md={3} key={challenge.challengeId}>
                  <ChallengeCard challenge={challenge} />
                </Grid>
                  ))
                )
              : (
                  Object.keys(challenges).map((division) => (
                <Box key={division} sx={{ mb: 2, ml: 3, width: '100%' }}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    {division}
                  </Typography>
                  <Grid container spacing={3}>
                    {challenges[division].map((challenge) => (
                      <Grid item xs={12} sm={6} md={3} key={challenge.challengeId}>
                        <ChallengeCard challenge={challenge} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                  ))
                )}
          </Grid>
        </Box>
      </AccordionDetails>
    </Accordion>
  )

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {renderChallenges(doneChallenges, 'Done Challenges', 'done')}
      {renderChallenges(notDoneChallenges, 'Not Done Challenges', 'notDone')}
      <Drawer anchor="right" open={!!openSections.info} onClose={() => toggleSection('info')}></Drawer>
    </Container>
  )
}

const ChallengeTips = ({ combinationTips, challenges }) => {
  const challengeIdsMap = { 'ARAM Champion': 101300, 'ARAM Finesse': 101200, 'ARAM Warrior': 101100 }
  const [openSections, setOpenSections] = useState({})
  const [isMainAccordionOpen, setIsMainAccordionOpen] = useState(true)

  const toggleSection = (section) => {
    setOpenSections((prevState) => ({
      ...prevState,
      [section]: !prevState[section]
    }))
  }

  if (!combinationTips || combinationTips.length === 0) {
    return <Typography variant="h6" color="textSecondary">No combination tips available</Typography>
  }

  const aramAuthority = challenges.find(challenge => challenge.challengeId === 101000)
  if (aramAuthority.level === 'MASTER') {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80px' }}>
          <Typography variant="h5" sx={{ color: '#333' }}>ARAM GOD is achieved in this account.</Typography>
        </Paper>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Accordion
        expanded={isMainAccordionOpen}
        onChange={() => setIsMainAccordionOpen((prev) => !prev)}
        sx={{
          mb: 2,
          borderRadius: 2,
          boxShadow: 3,
          background: 'linear-gradient(145deg, #e0e0e0, #c0c0c0)',
          '&:hover': {
            background: 'linear-gradient(145deg, #c0c0c0, #e0e0e0)'
          }
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ color: '#333', flexGrow: 1 }}>
            Tips to master the Challenges
          </Typography>
          <Tooltip title="Shows the combinations of challenges to upgrade and how many times to upgrade them, in order to get the respective challenge to master. E.g if you are silver on All Random All Champions and it says 2, means you need to reach to platfinum" arrow>
            <IconButton>
              <Info color="primary" style={{ fontSize: '1.5rem' }} />
            </IconButton>
          </Tooltip>
        </AccordionSummary>
        <AccordionDetails>
          {combinationTips.map((challenge, index) => {
            const section = `section-${index}`
            return (
              <Accordion
                key={index}
                expanded={!!openSections[section]}
                onChange={() => toggleSection(section)}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  boxShadow: 3,
                  background: 'linear-gradient(145deg, #f0f0f0, #cacaca)',
                  '&:hover': {
                    background: 'linear-gradient(145deg, #cacaca, #f0f0f0)'
                  }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Badge
                    badgeContent={<Stars />}
                    color="secondary"
                    sx={{ mr: 2 }}
                  >
                    <img
                      src={`/photos/challenge/${challengeIdsMap[challenge.challengeName]}-MASTER.png`}
                      alt={challenge.challengeName}
                      style={{ width: '50px', height: '50px', borderRadius: '50%' }}
                    />
                  </Badge>
                  <Typography variant="h6" sx={{ color: '#333' }}>{challenge.challengeName}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ flexGrow: 1 }}>
                    {challenge.combinations.length > 0
                      ? (
                      <Grid container spacing={3}>
                        {challenge.combinations.map((combination, idx) => (
                          <Grid item xs={12} sm={6} md={4} key={idx}>
                            <Card sx={{
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              boxShadow: 4,
                              borderRadius: 2,
                              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                              '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: 6
                              },
                              background: 'linear-gradient(135deg, #ffffff, #e0e0e0)'
                            }}>
                              <CardContent sx={{ padding: 3 }}>
                                <List>
                                  {Object.entries(combination.upgrades).map(([upgradeName, level], i) => (
                                    <ListItem key={i} sx={{
                                      padding: '8px 16px',
                                      background: 'rgba(255, 255, 255, 0.9)',
                                      borderRadius: 1,
                                      mb: 1,
                                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                      display: 'flex',
                                      justifyContent: 'space-between'
                                    }}>
                                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#555' }}>
                                        {upgradeName}
                                      </Typography>
                                      <Typography variant="body1" sx={{ color: '#ff5722' }}>
                                        {level}
                                      </Typography>
                                    </ListItem>
                                  ))}
                                </List>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="body2" sx={{ color: '#555', fontWeight: 'bold', textAlign: 'center', background: '#f5f5f5', borderRadius: 1, padding: 1 }}>
                                  Matches for Next Level: <span style={{ color: '#ff5722', fontSize: '1.2em' }}>{combination.matchesForNextLevel || 'N/A'}</span>
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                        )
                      : (
                      <Typography variant="h5" sx={{ textAlign: 'center', color: '#ff5722' }}>
                        Not enough information
                      </Typography>
                        )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )
          })}
        </AccordionDetails>
      </Accordion>
    </Container>
  )
}

export const AramGodInfo = ({ data }) => {
  return (
    <>
      {Object.keys(data).length > 0 && (
        <div>
          <div className="alert">
            <strong>Notice:</strong> For both challenges starting with `All Random All`, it takes an average of 3 matches per missing point to reach the next level due to current limitations.
          </div>
          <div style={{ width: '90%', height: 'auto', margin: 'auto' }}>
            <ChallengeTips combinationTips={data.combinationTips} challenges={data.challenges} />
          </div>
          <div style={{ width: '90%', height: 'auto', margin: 'auto' }}>
            <ChallengesList challenges={data.challenges} />
          </div>
        </div>
      )}
    </>
  )
}
